import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, User, Info, Clock, ChevronDown, ChevronUp, Download,
    MessageSquare, Users, Check, SlidersHorizontal, BarChart3, ShieldCheck
} from 'lucide-react';
import { fetchManagerInboxRequest, updateSkillEvalRequest, managerDecisionRequest, createSkillEvalRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { getUserRequest, getDirectReportsRequest } from '../../../redux/actions/userActions';
import { fetchJobSkillsRequest } from '../../../redux/actions/jobSkillActions';
import Team4Box from '../components/Team4Box';
import EvalN2 from './EvalN2';
import '../EvaluationDesign.css';
import { API_BASE_URL } from '../../../../api';

const NIVEAUX = [
    { value: 1, label: "Débutant" },
    { value: 2, label: "Intermédiaire" },
    { value: 3, label: "Avancé" },
    { value: 4, label: "Expert" }
];

export default function EvalEquipe({ initialTab = "direct" }) {
    const dispatch = useDispatch();
    const { inboxManager, loading } = useSelector(state => state.skillEvaluation || { inboxManager: [], loading: false });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { user } = useSelector(state => state.auth || {});
    const { users, directReports } = useSelector(state => state.user || { users: [], directReports: [] });
    const { jobSkills } = useSelector(state => state.jobSkill || { jobSkills: [] });

    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [filterStatus, setFilterStatus] = useState("EN_ATTENTE_MANAGER");
    const [viewMode, setViewMode] = useState("LISTE"); // LISTE or MATRICE
    const [pendingDecision, setPendingDecision] = useState(null);
    const [globalComment, setGlobalComment] = useState("");
    const [managerEvals, setManagerEvals] = useState({});
    const [managerComments, setManagerComments] = useState({});
    const [activeScope, setActiveScope] = useState(initialTab);

    const handleDownloadDictionnaire = useCallback(() => {
        window.open(`${API_BASE_URL}/api/campagnes/skills-dictionary/download`, "_blank");
    }, []);

    useEffect(() => {
        dispatch(fetchManagerInboxRequest());
        dispatch(fetchCampagnesRequest());
        if (user?.id) {
            dispatch(getDirectReportsRequest(user.id));
        }
    }, [dispatch, user?.id]);

    const activeCampagnes = useMemo(() =>
            (campagnesList || []).filter(c => c.status === true),
        [campagnesList]
    );
    
    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    useEffect(() => {
        if (selectedUser) {
            const evals = {};
            const comments = {};
            
            // 1. Load existing evaluation levels
            selectedUser.niveaux?.forEach(n => {
                evals[n.skillId] = n.scoreObtenu || n.scoreCollab || 1;
                comments[n.skillId] = n.commentaire || "";
            });

            // 2. Initialize Direct Manager Eval skills if not present
            jobSkills.forEach(js => {
                if ((js.modeEvaluationNom === "Évaluation du manager" || js.modeEvaluationCode === "MANAGER_EVAL") && !evals[js.skillId]) {
                    evals[js.skillId] = 1; // Default starting level
                }
            });

            setManagerEvals(evals);
            setManagerComments(comments);
            setGlobalComment(selectedUser.managerComment || "");
            setViewMode("LISTE"); // Force return to list when a user is selected
        }
    }, [selectedUser, dispatch, jobSkills]);

    useEffect(() => {
        if (selectedUser?.jobId) {
            dispatch(fetchJobSkillsRequest(selectedUser.jobId));
        }
    }, [selectedUser?.jobId, dispatch]);

    useEffect(() => {
        if (pendingDecision && !loading) {
            dispatch(managerDecisionRequest(pendingDecision.id, pendingDecision.dto));
            setPendingDecision(null);
            setSelectedUser(null);
        }
    }, [loading, pendingDecision, dispatch]);

    const handleEval = (compId, level) => setManagerEvals(p => ({ ...p, [compId]: level }));
    const handleComment = (compId, text) => setManagerComments(p => ({ ...p, [compId]: text }));

    const handleApprove = () => {
        if (!selectedUser || selectedUser.isVirtual) return;
        
        const niveauxArray = Object.entries(managerEvals).map(([skillId, score]) => ({
            skillId: parseInt(skillId),
            scoreManager: parseInt(score),
            scoreObtenu: parseInt(score),
            commentaire: managerComments[skillId] || ""
        }));

        const updateDto = {
            campagneId: selectedUser.campagneId,
            userId: selectedUser.userId,
            statut: selectedUser.statut,
            evaluateurId: user?.id || user?.uid,
            dateEvaluation: selectedUser.dateEvaluation,
            commentaireGlobal: globalComment, 
            niveaux: niveauxArray
        };
        dispatch(updateSkillEvalRequest(selectedUser.id, updateDto));
        
        const decisionDto = {
            approved: true,
            comment: globalComment || "Évalué et validé par le manager"
        };
        setPendingDecision({ id: selectedUser.id, dto: decisionDto });
    };

    const managedCollaborators = useMemo(() => {
        if (!directReports) return [];
        
        return directReports.map(report => {
            const evaluation = (inboxManager || []).find(e => 
                e.userId === report.id && 
                (!selectedCampagneId || e.campagneId === Number(selectedCampagneId))
            );
            return {
                id: report.id,
                userId: report.id,
                fullName: report.fullName,
                profilePictureUrl: report.profilePictureUrl,
                evaluation: evaluation || null,
                statut: evaluation?.statut || 'NOT_STARTED',
                jobId: report.jobId,
                initiales: report.fullName
                    ? report.fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
                    : "??"
            };
        });
    }, [directReports, inboxManager, selectedCampagneId]);

    const filteredCollaborators = useMemo(() => {
        return managedCollaborators.filter(c => {
            const matchSearch = c.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
            if (filterStatus === 'ALL') return matchSearch;
            const sName = typeof c.statut === 'string' ? c.statut : c.statut?.name;
            if (filterStatus === 'PENDING') return matchSearch && sName === 'EN_ATTENTE_MANAGER';
            return matchSearch && sName === filterStatus;
        });
    }, [managedCollaborators, searchQuery, filterStatus]);

    const getStatutConfig = (statut) => {
        const sName = typeof statut === 'string' ? statut : statut?.name;
        switch (sName) {
            case "NOT_STARTED":        return { label: "Non débutée",    cls: "eval-badge-grey",  icon: <Clock size={12} />, color: '#94a3b8' };
            case "EN_ATTENTE_COLLAB":  return { label: "Attente Collab.", cls: "eval-badge-grey",  icon: <Clock size={12} />, color: '#f59e0b' };
            case "EN_ATTENTE_MANAGER": return { label: "À traiter",        cls: "eval-badge-red",   icon: <Info size={12} />, color: '#ef4444' };
            case "EN_ATTENTE_N2":      return { label: "Arbitrage N+2",  cls: "eval-badge-blue", icon: <Check size={12} />, color: '#3b82f6' };
            case "VALIDEE":            return { label: "Terminée",        cls: "eval-badge-green", icon: <Check size={12} />, color: '#10b981' };
            default:                   return { label: sName || 'Inconnu', cls: "eval-badge-grey", icon: null, color: '#64748b' };
        }
    };

    const formattedAxes = useMemo(() => {
        const existingNiveaux = selectedUser?.niveaux || [];
        const allRelevantJobSkills = jobSkills || [];

        const autoEvalSkills = [];
        const directManagerSkills = [];
        const peer360Skills = [];

        // Build list from job profile
        allRelevantJobSkills.forEach(js => {
            const code = js.modeEvaluationCode?.toUpperCase() || "";
            const name = js.modeEvaluationNom?.toLowerCase() || "";
            
            const isDirectManagerMode = code.includes("MANAGER_EVAL") || name.includes("direct");
            const isPeer360Mode = code.includes("PAIRES") || code.includes("360") || name.includes("paires") || name.includes("360");
            
            const existingEval = existingNiveaux.find(n => n.skillId === js.skillId);
            
            const compData = {
                id: js.skillId,
                nom: js.skillNom,
                description: js.skillCode || "",
                scoreCollab: existingEval?.scoreCollab ?? null,
                scoreManager: existingEval?.scoreManager ?? null,
                scoreMoyenPairs: existingEval?.scoreMoyenPairs ?? null,
                autoEvalComment: existingEval?.commentaire || "",
                isDirectManagerEval: isDirectManagerMode,
                isPeer360Eval: isPeer360Mode
            };

            if (isPeer360Mode) {
                peer360Skills.push(compData);
            } else if (isDirectManagerMode) {
                directManagerSkills.push(compData);
            } else {
                autoEvalSkills.push(compData);
            }
        });

        // Add any existing evaluation levels not in job profile
        existingNiveaux.forEach(n => {
            if (!allRelevantJobSkills.some(js => js.skillId === n.skillId)) {
                autoEvalSkills.push({
                    id: n.skillId,
                    nom: n.skillNom,
                    description: n.skillCode || "",
                    scoreCollab: n.scoreCollab,
                    scoreManager: n.scoreManager,
                    scoreMoyenPairs: n.scoreMoyenPairs,
                    autoEvalComment: n.commentaire || "",
                    isDirectManagerEval: false,
                    isPeer360Eval: false
                });
            }
        });

        const groups = [];
        if (autoEvalSkills.length > 0) {
            groups.push({
                id: "AUTO_EVAL",
                nom: "Auto-évaluation Collaborateur",
                icon: <User size={18} />,
                color: "#ff8c00",
                competences: autoEvalSkills
            });
        }
        if (directManagerSkills.length > 0) {
            groups.push({
                id: "DIRECT",
                nom: "Évaluation Directe Manager",
                icon: <Check size={18} />,
                color: "#ee645f",
                competences: directManagerSkills
            });
        }
        if (peer360Skills.length > 0) {
            groups.push({
                id: "PEER_360",
                nom: "Évaluation 360° (Collègues)",
                icon: <Users size={18} />,
                color: "#204385",
                competences: peer360Skills
            });
        }
        return groups;
    }, [selectedUser, jobSkills]);

    const isVirtual = selectedUser?.isVirtual;
    const sName = typeof selectedUser?.statut === 'string' ? selectedUser.statut : selectedUser?.statut?.name;
    const isEditable = selectedUser && sName === 'EN_ATTENTE_MANAGER';
    const canApprove = isEditable;

    if (activeScope === "n2") {
        return (
            <div className="eval-page eval-page-unified">
                <div className="eval-header eval-header-rich">
                    <div>
                        <h1>Évaluation d'équipe</h1>
                    </div>
                    <div className="eval-header-actions">
                        <button className="ae-btn-dict" onClick={handleDownloadDictionnaire} type="button">
                            <Download size={12} />
                            Télécharger le dictionnaire de compétences
                        </button>
                        <div className="eval-module-tabs">
                            <button className={`eval-module-tab ${activeScope === 'direct' ? 'active' : ''}`} onClick={() => setActiveScope('direct')}>
                                <Users size={15} /> Équipe directe
                            </button>
                            <button className={`eval-module-tab ${activeScope === 'n2' ? 'active' : ''}`} onClick={() => setActiveScope('n2')}>
                                <ShieldCheck size={15} /> N+2
                            </button>
                        </div>
                    </div>
                </div>
                <EvalN2 embedded />
            </div>
        );
    }

    return (
        <div className="eval-page eval-page-unified">

            {/* Header */}
            <div className="eval-header eval-header-rich">
                <div>
                    <h1>Évaluation d'equipe</h1>
                    <p>Validez et consolidez les competences de vos collaborateurs.</p>
                </div>
                <div className="eval-header-actions">
                    <button className="ae-btn-dict" onClick={handleDownloadDictionnaire} type="button">
                        <Download size={12} />
                        Télécharger le dictionnaire de compétences
                    </button>
                    <div className="eval-module-tabs">
                        <button className={`eval-module-tab ${activeScope === 'direct' ? 'active' : ''}`} onClick={() => setActiveScope('direct')}>
                            <Users size={15} /> Équipe directe
                        </button>
                        <button className={`eval-module-tab ${activeScope === 'n2' ? 'active' : ''}`} onClick={() => setActiveScope('n2')}>
                            <ShieldCheck size={15} /> N-2
                        </button>
                    </div>
                </div>
                {/*<div className="eval-filter-tabs">*/}
                {/*    <button className={`eval-tab-btn ${viewMode === 'LISTE' && filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => { setViewMode('LISTE'); setFilterStatus('ALL'); setSelectedUser(null); }}>Toute l'équipe</button>*/}
                {/*    <button className={`eval-tab-btn ${viewMode === 'LISTE' && filterStatus === 'PENDING' ? 'active' : ''}`} onClick={() => { setViewMode('LISTE'); setFilterStatus('PENDING'); setSelectedUser(null); }}>À traiter</button>*/}
                {/*    <button className={`eval-tab-btn ${viewMode === 'LISTE' && filterStatus === 'VALIDEE' ? 'active' : ''}`} onClick={() => { setViewMode('LISTE'); setFilterStatus('VALIDEE'); setSelectedUser(null); }}>Archives</button>*/}
                {/*    <button className={`eval-tab-btn ${viewMode === 'MATRICE' ? 'active' : ''}`} onClick={() => setViewMode('MATRICE')}>*/}
                {/*        <BarChart3 size={14} style={{ marginRight: '6px' }} /> Matrice 4-Box*/}
                {/*    </button>*/}
                {/*</div>*/}
            </div>

            {/* Campaign Bar */}
            <div className="eval-campaign-bar">
                <span className="eval-campaign-label">Campagne active :</span>
                {loading ? (
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Chargement…</span>
                ) : (
                    <select className="eval-select" value={selectedCampagneId} onChange={e => setSelectedCampagneId(e.target.value)}>
                        <option value="">-- Toutes les campagnes --</option>
                        {activeCampagnes.map(c => (
                            <option key={c.id} value={c.id}>{c.nom}{c.anneeCampagne ? ` (${c.anneeCampagne})` : ''}</option>
                        ))}
                    </select>
                )}
            </div>

            <div className="eval-layout">

                {/* Sidebar */}
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search size={16} className="eval-search-icon" />
                            <input
                                type="text"
                                className="eval-input"
                                placeholder="Rechercher un collaborateur…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="eval-list">
                        {loading && managedCollaborators.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Chargement…</div>
                        ) : filteredCollaborators.length === 0 ? (
                            <div className="eval-empty" style={{ padding: '20px' }}>
                                <Users size={32} className="eval-empty-icon" />
                                <p>Aucun collaborateur trouvé</p>
                            </div>
                        ) : filteredCollaborators.map(c => {
                            const st = getStatutConfig(c.statut);
                            const isActive = selectedUser?.userId === c.userId;
                            return (
                                <div key={c.userId} className={`eval-user-card ${isActive ? 'active' : ''}`} onClick={() => setSelectedUser({ ...c, ...(c.evaluation || {}), isVirtual: !c.evaluation })}>
                                    <div className="eval-avatar" style={{ background: st.color }}>{c.initiales}</div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{c.fullName}</div>
                                        <div className="eval-user-sub">{c.evaluation?.campagneNom || "Nouvelle évaluation"}</div>
                                        <span className={`eval-badge ${st.cls}`}>{st.icon} {st.label}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* Main Content */}
                <main className="eval-main">
                    {viewMode === 'MATRICE' ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <BarChart3 size={18} /> Vision d'ensemble de l'équipe
                                </div>
                                <Team4Box 
                                    teamData={managedCollaborators.map(c => c.evaluation).filter(Boolean)} 
                                    onUserSelect={(u) => { setViewMode("LISTE"); setSelectedUser(u); }} 
                                />
                            </div>
                        </div>
                    ) : !selectedUser ? (
                        <div className="eval-empty" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <SlidersHorizontal size={64} className="eval-empty-icon" />
                            <h3>Prêt à évaluer ?</h3>
                            <p>Sélectionnez un collaborateur dans la liste pour commencer l'arbitrage.</p>
                        </div>
                    ) : (
                        <div className="eval-content">

                            {/* Profile Header Card */}
                            <div className="eval-card" style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                        <div className="eval-avatar" style={{ width: '56px', height: '56px', fontSize: '20px', background: sName === 'VALIDEE' ? '#10b981' : '#204385' }}>
                                            {selectedUser.initiales || (selectedUser.userFullName || selectedUser.fullName || "?")[0]}
                                        </div>
                                        <div>
                                            <h2 style={{ margin: 0, color: '#204385', fontSize: '20px' }}>{selectedUser.userFullName || selectedUser.fullName}</h2>
                                            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                                                {selectedUser.userEmployeeCode || "Collaborateur"} • {selectedUser.campagneNom || "Nouvelle session"}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        {canApprove ? (
                                            <button className="eval-btn eval-btn-coral" onClick={handleApprove} disabled={loading}>
                                                <Check size={16} /> {loading ? 'Validation…' : 'Valider l\'évaluation'}
                                            </button>
                                        ) : (
                                            !isVirtual && sName === 'EN_ATTENTE_COLLAB' ? (
                                                <div className="eval-badge eval-badge-orange" style={{ padding: '8px 16px', background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' }}>
                                                    En attente d'autoévaluation
                                                </div>
                                            ) : isVirtual || sName === 'NOT_STARTED' ? (
                                                <div className="eval-badge eval-badge-grey" style={{ padding: '8px 16px', background: '#f1f5f9', color: '#64748b' }}>
                                                    Évaluation non démarrée
                                                </div>
                                            ) : null
                                        )}
                                    </div>
                                </div>
                            </div>

                            {(!isEditable && sName !== 'VALIDEE') && (
                                <div className="eval-card" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', textAlign: 'center', padding: '24px', marginBottom: '20px' }}>
                                    <Clock size={32} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                                    <h3 style={{ margin: '0 0 8px', color: '#475569' }}>En attente du collaborateur</h3>
                                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                                        Vous ne pouvez pas encore évaluer formellement ce dossier car le collaborateur doit d'abord soumettre son auto-évaluation. Vous pouvez néanmoins consulter ci-dessous les compétences prévues pour ce rôle.
                                    </p>
                                </div>
                            )}

                            {/* Collab general comment */}
                            {selectedUser.commentaireGlobal && (
                                <div className="eval-card" style={{ borderLeft: '4px solid #ee645f' }}>
                                    <div className="eval-section-title">Commentaire Global Collaborateur</div>
                                    <p style={{ fontStyle: 'italic', fontSize: '13px', color: '#475569', margin: 0 }}>"{selectedUser.commentaireGlobal}"</p>
                                </div>
                            )}

                            {/* Manager Appreciation */}
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <MessageSquare size={18} /> Appréciation Générale Manager
                                </div>
                                <textarea
                                    className="eval-textarea"
                                    placeholder="Saisissez votre appréciation synthétique sur l'année écoulée..."
                                    value={globalComment}
                                    onChange={e => isEditable && setGlobalComment(e.target.value)}
                                    readOnly={!isEditable}
                                    rows={3}
                                />
                            </div>

                            {/* Competencies */}
                            <div className="eval-card">
                                {/* Competencies by Section */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                    {formattedAxes.map(axe => (
                                        <div key={axe.id} className="eval-section-group">
                                            {/* Section Header */}
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '12px', 
                                                marginBottom: '16px',
                                                padding: '0 8px'
                                            }}>
                                                <div style={{ 
                                                    background: axe.color, 
                                                    color: '#fff', 
                                                    width: '32px', 
                                                    height: '32px', 
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: `0 4px 12px ${axe.color}40`
                                                }}>
                                                    {axe.icon}
                                                </div>
                                                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800' }}>{axe.nom}</h3>
                                                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, #e2e8f0 0%, transparent 100%)', marginLeft: '12px' }}></div>
                                            </div>

                                            <div style={{ display: 'grid', gap: '16px' }}>
                                                {axe.competences.map(comp => (
                                                    <div key={comp.id} className="eval-card" style={{ padding: '0', overflow: 'hidden', border: '1px solid #e2e8f0', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                                                        {/* Card Header */}
                                                        <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{comp.nom}</span>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                {axe.id === 'AUTO_EVAL' && comp.scoreCollab !== null && (
                                                                    <div className="eval-badge" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' }}>
                                                                        Auto-éval: <strong>{comp.scoreCollab}</strong>
                                                                    </div>
                                                                )}
                                                                {axe.id === 'PEER_360' && comp.scoreMoyenPairs !== null && (
                                                                    <div style={{ position: 'relative' }}>
                                                                        <div className="eval-badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            360°: <strong>{comp.scoreMoyenPairs}</strong>
                                                                            <Info size={12} style={{ cursor: 'help' }} className="peer-detail-trigger" />
                                                                        </div>
                                                                        {/* Tooltip detail (visible on trigger hover) */}
                                                                        <div className="peer-detail-tooltip">
                                                                            <div style={{ fontWeight: 800, borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', marginBottom: '6px', fontSize: '11px' }}>Détails des avis</div>
                                                                            {selectedUser.consolidatedSkills?.find(cs => cs.skillId === comp.id)?.peerDetails?.map((pd, i) => (
                                                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '10px', marginBottom: '2px' }}>
                                                                                    <span>{pd.evaluatorName} ({pd.role})</span>
                                                                                    <strong style={{ color: '#1d4ed8' }}>{pd.score}</strong>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Card Body */}
                                                        <div className="eval-grid" style={{ 
                                                            padding: '16px', 
                                                            gap: '24px', 
                                                            display: 'grid', 
                                                            gridTemplateColumns: axe.id === 'DIRECT' ? '1fr' : '1fr 1.5fr' 
                                                        }}>
                                                            {/* Show Context (Collab Comment or Peer Vision) */}
                                                            {axe.id === 'AUTO_EVAL' && (
                                                                <div className="eval-collab-context">
                                                                    <div className="eval-section-title" style={{ fontSize: '11px', marginBottom: '8px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                        Observation du Collaborateur
                                                                    </div>
                                                                    <div style={{ 
                                                                        background: '#fffbeb', 
                                                                        border: '1px solid #fef3c7', 
                                                                        borderRadius: '8px', 
                                                                        padding: '12px',
                                                                        color: '#92400e',
                                                                        fontSize: '13px',
                                                                        fontStyle: 'italic',
                                                                        minHeight: '80px',
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        {comp.autoEvalComment || "Aucun commentaire spécifique."}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {axe.id === 'PEER_360' && (
                                                                <div className="eval-360-view">
                                                                    <div className="eval-section-title" style={{ fontSize: '11px', marginBottom: '8px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                        Vision 360° (Collègues)
                                                                    </div>
                                                                    <div style={{ 
                                                                        background: '#f0f7ff', 
                                                                        border: '1px solid #e0efff', 
                                                                        borderRadius: '8px', 
                                                                        padding: '12px',
                                                                        textAlign: 'center',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'center',
                                                                        minHeight: '80px'
                                                                    }}>
                                                                        {comp.scoreMoyenPairs !== null ? (
                                                                            <>
                                                                                <div style={{ fontSize: '28px', fontWeight: '800', color: '#1d4ed8', lineHeight: '1' }}>{comp.scoreMoyenPairs}</div>
                                                                                <div style={{ fontSize: '10px', color: '#3b82f6', fontWeight: '700', marginTop: '6px' }}>MOYENNE DES PAIRS</div>
                                                                            </>
                                                                        ) : (
                                                                            <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Aucune donnée 360°</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Manager Arbitrage (Always present) */}
                                                            <div className="eval-manager-view">
                                                                <div className="eval-section-title" style={{ fontSize: '11px', marginBottom: '8px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    Arbitrage Manager
                                                                </div>
                                                                <div className="eval-input-group" style={{ 
                                                                    background: '#f8fafc', 
                                                                    padding: '12px', 
                                                                    borderRadius: '8px', 
                                                                    border: '1px solid #e2e8f0',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                                        {NIVEAUX.map(niv => (
                                                                            <button
                                                                                key={niv.value}
                                                                                className={`eval-choice-btn ${managerEvals[comp.id] === niv.value ? 'active' : ''}`}
                                                                                onClick={() => isEditable && handleEval(comp.id, niv.value)}
                                                                                style={{ 
                                                                                    cursor: isEditable ? 'pointer' : 'default',
                                                                                    width: '36px',
                                                                                    height: '36px',
                                                                                    borderRadius: '8px',
                                                                                    fontWeight: '700'
                                                                                }}
                                                                                title={niv.label}
                                                                            >
                                                                                {niv.value}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                                                                        {NIVEAUX.find(n => n.value === managerEvals[comp.id])?.label || "Non évalué"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
